import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { residentUsers } from "@/mocks/users";

const gradeColor = (g: string) => {
  if (g === "A+") return "bg-green-100 text-green-700";
  if (g === "A") return "bg-teal-100 text-teal-700";
  if (g === "B") return "bg-amber-100 text-amber-700";
  return "bg-red-100 text-red-700";
};

interface Props {
  search: string;
  filterStatus: string;
}

export default function ResidentTable({ search, filterStatus }: Props) {
  const navigate = useNavigate();
  const [purchaseModal, setPurchaseModal] = useState<typeof residentUsers[0] | null>(null);
  const [purchaseAmt, setPurchaseAmt] = useState(100);
  const [purchaseDone, setPurchaseDone] = useState(false);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const filtered = residentUsers.filter(u => {
    const matchSearch = !search || u.name.includes(search) || u.phone.includes(search) || u.id.includes(search);
    const matchStatus = filterStatus === "全部" || u.status === filterStatus;
    return matchSearch && matchStatus;
  });

  const totalPages = Math.ceil(filtered.length / pageSize);
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handlePurchase = () => {
    setPurchaseDone(true);
    setTimeout(() => {
      setPurchaseModal(null);
      setPurchaseDone(false);
    }, 1500);
  };

  return (
    <>
      <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
          <span className="text-sm font-medium text-gray-700">共 {filtered.length} 条记录</span>
          <div className="flex gap-2">
            <button className="flex items-center gap-1.5 text-xs text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-download-line"></i>导出Excel
            </button>
            <button className="flex items-center gap-1.5 text-xs text-gray-500 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50 cursor-pointer whitespace-nowrap">
              <i className="ri-printer-line"></i>打印
            </button>
          </div>
        </div>
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              {["用户编号", "姓名", "手机号", "地址", "本月用气", "账户余额", "安全等级", "状态", "操作"].map(h => (
                <th key={h} className="text-left text-xs text-gray-500 font-medium px-4 py-3">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {paged.map(user => (
              <tr key={user.id} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3 text-xs text-gray-500 font-mono">{user.id}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 rounded-full bg-orange-100 flex items-center justify-center flex-shrink-0">
                      <span className="text-xs text-[#E8531A] font-bold">{user.name[0]}</span>
                    </div>
                    <span className="text-sm font-medium text-gray-800">{user.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-600">{user.phone}</td>
                <td className="px-4 py-3 text-xs text-gray-500 max-w-36 truncate">{user.address}</td>
                <td className="px-4 py-3 text-sm text-gray-700 font-medium">{user.monthlyUsage} m³</td>
                <td className="px-4 py-3">
                  <span className={`text-sm font-bold ${user.balance < 100 ? "text-red-600" : "text-gray-800"}`}>
                    ¥{user.balance.toFixed(2)}
                  </span>
                  {user.balance < 50 && (
                    <div className="text-[10px] text-red-400">余额不足</div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${gradeColor(user.safetyGrade)}`}>
                    {user.safetyGrade} ({user.safetyLevel}分)
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${
                    user.status === "正常" ? "bg-green-50 text-green-600" :
                    user.status === "欠费预警" ? "bg-amber-50 text-amber-600" :
                    "bg-red-50 text-red-600"
                  }`}>
                    {user.status}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button onClick={() => navigate(`/profile/${user.id}`)} className="text-xs text-[#E8531A] hover:underline cursor-pointer whitespace-nowrap">详情</button>
                    <button onClick={() => { setPurchaseModal(user); setPurchaseDone(false); }} className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap">购气</button>
                    <button className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer whitespace-nowrap">短信</button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-gray-50">
          <span className="text-xs text-gray-400">第 {page} / {totalPages} 页</span>
          <div className="flex gap-1">
            <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 cursor-pointer whitespace-nowrap">上一页</button>
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => i + 1).map(p => (
              <button key={p} onClick={() => setPage(p)} className={`px-3 py-1.5 text-xs rounded-lg cursor-pointer whitespace-nowrap ${page === p ? "bg-[#E8531A] text-white" : "border border-gray-200 hover:bg-gray-50"}`}>{p}</button>
            ))}
            <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50 cursor-pointer whitespace-nowrap">下一页</button>
          </div>
        </div>
      </div>

      {/* Purchase Modal */}
      {purchaseModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50" onClick={() => setPurchaseModal(null)}>
          <div className="bg-white rounded-2xl p-6 w-[400px]" onClick={e => e.stopPropagation()}>
            {purchaseDone ? (
              <div className="text-center py-4">
                <div className="w-16 h-16 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                  <i className="ri-check-double-line text-green-500 text-3xl"></i>
                </div>
                <div className="text-base font-bold text-gray-900">购气成功！</div>
                <div className="text-sm text-gray-500 mt-1">已为 {purchaseModal.name} 充值 ¥{purchaseAmt}</div>
              </div>
            ) : (
              <>
                <h3 className="text-base font-bold text-gray-900 mb-1">为用户购气</h3>
                <p className="text-xs text-gray-400 mb-4">{purchaseModal.name} · {purchaseModal.id} · 当前余额 ¥{purchaseModal.balance.toFixed(2)}</p>
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {[50, 100, 200, 500].map(amt => (
                    <button key={amt} onClick={() => setPurchaseAmt(amt)} className={`py-2.5 rounded-xl text-sm font-bold border-2 cursor-pointer whitespace-nowrap ${purchaseAmt === amt ? "border-[#E8531A] bg-orange-50 text-[#E8531A]" : "border-gray-100 text-gray-700"}`}>¥{amt}</button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setPurchaseModal(null)} className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap">取消</button>
                  <button onClick={handlePurchase} className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap">确认购气</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
